import type { Role } from "../api/client";
import { BackIcon, CheckIcon, LockIcon } from "../components/Icons";
import { AUDIT_STEPS, currentStep, reach, type StepKey } from "./steps";

/**
 * The audit's step bar — six chevrons over the specification's thirteen steps.
 *
 * What each stop *means* is in `steps.ts`; this draws it.
 *
 * **The stops are joined again, and the numbering they carried is gone.** They were six
 * separate marks with a mono eyebrow citing §5 — `Step 1`, `Steps 4–5`, `Steps 10–13` —
 * and the eyebrow was the reason the mark could not be a numeral: a sixth segment numbered
 * `6` above a label reading `Steps 10–13` is two numbering schemes in one stop. With the
 * citation removed the numeral is unambiguous, because it now counts stops and nothing
 * else. §5's mapping is not lost; it is on `AUDIT_STEPS`, which is where a reader tracing a
 * stop back to the specification is already looking.
 *
 * **Done is behind the audit, not behind the user.** A segment is passed because the audit
 * has passed that step, so both parties see the same bar — a lead auditor who has never
 * opened the intake still sees the first stop ticked, because it is. The onboarding rail's
 * `furthest` index has no counterpart here and deliberately so: a wizard's high-water mark
 * is a fact about one person filling one form, and an audit is three parties and a state
 * machine.
 *
 * **A refused segment says why.** The `title` carries the reason and a padlock sits beside
 * the label where the stop is reachable but not writable for this role. A segment that
 * greys out silently is the kind of interface people work around, and the reasons here are
 * all regulatory — who owns the record, and what has already been conducted.
 *
 * **The way out is in the bar, not above it.** It was a separate link over the rail; it is
 * now the bar's first item, behind a rule, on the same reasoning M23 applied to the audit
 * day's banner — a reader's eye starts at the left edge, and a way out of a screen belongs
 * at the top of it rather than floating above the thing it leaves.
 *
 * ⚠ **The palette is `tokens.css`, not the mock this shape came from.** The design the
 * chevrons follow drew the current stop as a green gradient over a white ground with mint
 * accents; all three are on the prohibited list — no gradients, no pure white, no teal — so
 * the fill is flat Deep Forest, the ground is Off-White, and the passed stops are Green
 * Wash. Amber Light carries the numeral on the dark stop, which is the same three-colour
 * rule the banner settled on: off-white, its muted mix, and Amber Light.
 */
export function AuditStepBar({
  state,
  auditDay,
  role,
  active,
  onSelect,
  onBack,
  backLabel,
}: {
  /** `audits.state`. What may be reached is derived from this and nothing else. */
  state: string;
  /**
   * `audits.audit_day`. The second fact, and only *where the audit is* depends on it: the
   * state stops moving at `awaiting_documents`, so without it the rail marks pre-audit
   * readiness as *now* on a morning the client is opening the session.
   */
  auditDay: boolean;
  role: Role;
  /** The stop being shown. Not necessarily the audit's own — a step behind can be opened. */
  active: StepKey;
  onSelect: (step: StepKey) => void;
  /** Leaves the audit. The bar's first item, ahead of the rule. */
  onBack: () => void;
  backLabel: string;
}) {
  const here = currentStep(state, auditDay);
  const order = AUDIT_STEPS.findIndex((step) => step.key === here);

  return (
    <nav className="step" aria-label="Audit steps">
      <button type="button" className="stepback" onClick={onBack} title={backLabel}>
        <BackIcon size={15} />
        {backLabel}
      </button>
      <span className="steprule" aria-hidden="true" />

      <ol className="steplist">
        {AUDIT_STEPS.map((step, index) => {
          const verdict = reach(step.key, state, role);
          const done = index < order && verdict.reachable;
          const now = step.key === active;
          const readOnly = verdict.reachable && !verdict.writable;

          return (
            /*
              Each chevron's point overlaps the next one's tail, so they have to stack in
              reading order — leftmost on top — or the point of every stop is cut off by the
              stop after it.
            */
            <li key={step.key} style={{ zIndex: AUDIT_STEPS.length - index }}>
              <button
                type="button"
                className={`stepbtn${now ? " now" : done ? " done" : ""}`}
                disabled={!verdict.reachable}
                /*
                  The reason a segment refuses is the whole of what it has to say, so where
                  `reach` gives one it is the tooltip. Where it does not, the tooltip is
                  still the stop's own name — the labels truncate at narrow widths and a
                  hover that says nothing is worse than one that repeats.

                  Steps 4–5 is the one stop that is read-only for *both* parties, and it is
                  read-only because nobody edits there rather than because of who is asking
                  — so `reach` gives it no sentence. The marker is appended rather than left
                  to the padlock alone: the padlock is hidden at narrow widths, and a stop
                  whose only signal for not-writable has been dropped for want of room is a
                  stop that reads as writable.
                */
                title={verdict.why ?? `${step.title}${readOnly ? " · Read only." : ""}`}
                aria-current={now ? "step" : undefined}
                onClick={() => onSelect(step.key)}
              >
                <span className="stepmark" aria-hidden="true">
                  {done ? <CheckIcon size={14} /> : index + 1}
                </span>
                <span className="steptext">
                  {/*
                    Spoken rather than left to the padlock, which is `display: none` at
                    narrow widths and out of the accessibility tree with it.
                  */}
                  {readOnly ? <span className="visually-hidden">Read only.</span> : null}
                  <span className="steplabel">{step.title}</span>
                  {readOnly ? <LockIcon size={11} /> : null}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
